import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const PHASE_LABEL = {
    never: "Mai eseguito",
    starting: "Avvio…",
    teams_meta: "Recupero squadre & budget",
    rosters: "Importazione rose",
    done: "Completato",
    cancelled: "Annullato",
    error: "Errore",
};

export default function AdminLegacyImport() {
    const [status, setStatus] = useState(null);
    const [tmStatus, setTmStatus] = useState(null);
    const [histStatus, setHistStatus] = useState(null);
    const [legacyTeams, setLegacyTeams] = useState([]);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [username, setUsername] = useState("nicobenz");
    const [password, setPassword] = useState("");
    const [showCredentials, setShowCredentials] = useState(false);
    const [formMode, setFormMode] = useState("full"); // "full" | "history"
    const [tmMigBusy, setTmMigBusy] = useState(false);
    const [tmMigResult, setTmMigResult] = useState(null);
    const importFileRef = useRef(null);
    const pollRef = useRef(null);

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/legacy/status");
            setStatus(data);
        } catch {
            /* ignore */
        }
        try {
            const { data } = await api.get("/legacy/tm-status");
            setTmStatus(data);
        } catch {
            /* ignore */
        }
        try {
            const { data } = await api.get("/legacy/history-status");
            setHistStatus(data);
        } catch {
            /* ignore */
        }
    }, []);

    const fetchTeams = useCallback(async () => {
        try {
            const { data } = await api.get("/legacy/teams");
            setLegacyTeams(data || []);
        } catch {
            /* ignore */
        }
    }, []);

    /* eslint-disable */
    useEffect(() => {
        fetchStatus();
        fetchTeams();
        pollRef.current = setInterval(() => {
            fetchStatus();
            fetchTeams();
        }, 3000);
        return () => clearInterval(pollRef.current);
    }, [fetchStatus, fetchTeams]);
    /* eslint-enable */

    const startImport = async () => {
        setErr("");
        if (!username || !password) {
            setErr("Inserisci credenziali del vecchio sito");
            return;
        }
        setBusy(true);
        try {
            await api.post("/legacy/import", { username, password });
            setShowCredentials(false);
            setPassword("");
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const startTmRefresh = async () => {
        // No credentials needed — the op8 player endpoint is public.
        setErr("");
        setBusy(true);
        try {
            await api.post("/legacy/tm-refresh");
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const startHistoryImport = async () => {
        setErr("");
        if (!username || !password) {
            setErr("Inserisci credenziali del vecchio sito");
            return;
        }
        setBusy(true);
        try {
            await api.post("/legacy/history-import", { username, password });
            setShowCredentials(false);
            setPassword("");
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        try {
            await api.post("/legacy/cancel");
            await fetchStatus();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const exportTmValues = async () => {
        setErr(""); setTmMigBusy(true);
        try {
            const { data } = await api.get("/admin/tm-migration/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `tm-values-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            setTmMigResult({ kind: "export", count: data.count });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setTmMigBusy(false);
        }
    };

    const importTmValues = async (file) => {
        if (!file) return;
        setErr(""); setTmMigBusy(true); setTmMigResult(null);
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const values = Array.isArray(parsed) ? parsed : parsed.values;
            if (!Array.isArray(values)) throw new Error("File non valido: manca 'values'");
            const { data } = await api.post("/admin/tm-migration/import", { values });
            setTmMigResult({ kind: "import", ...data });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "File non valido");
        } finally {
            setTmMigBusy(false);
            if (importFileRef.current) importFileRef.current.value = "";
        }
    };

    const teamsPct =
        status?.teams_total && status?.teams_done
            ? Math.min(100, (status.teams_done / status.teams_total) * 100)
            : 0;

    return (
        <div data-testid="admin-legacy-import">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="op-heading text-3xl">Import Legacy</h2>
                    <p className="op-label mt-2">Importazione dal vecchio sito op8.zupper.it</p>
                </div>
                <div className="op-mono text-t8f95a3 text-xs">
                    Fonte: op8.zupper.it
                </div>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-4" data-testid="legacy-error">
                    {err}
                </div>
            )}

            {/* Status panel */}
            <div className="grid grid-cols-2 lg:grid-cols-4 border border-t22252a divide-x divide-y lg:divide-y-0 divide-t22252a mb-6">
                <Stat
                    label="Stato"
                    value={PHASE_LABEL[status?.phase] || "—"}
                    accent={status?.running ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-8f95a3))"}
                />
                <Stat
                    label="Squadre completate"
                    value={`${status?.teams_done ?? 0}${status?.teams_total ? ` / ${status.teams_total}` : ""}`}
                />
                <Stat
                    label="Giocatori importati"
                    value={(status?.players_imported ?? 0).toLocaleString("it-IT")}
                />
                <Stat
                    label="Roster entries totali"
                    value={(status?.roster_entries ?? 0).toLocaleString("it-IT")}
                />
            </div>

            {/* Progress bar */}
            {status?.running && status?.teams_total > 0 && (
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="op-label">Avanzamento</span>
                        <span className="op-mono text-sm text-tffffff">
                            {status.teams_done.toLocaleString("it-IT")} / {status.teams_total.toLocaleString("it-IT")} squadre ({teamsPct.toFixed(1)}%)
                        </span>
                    </div>
                    <div className="h-2 bg-t1a1d24 border border-t22252a">
                        <div
                            className="h-full bg-t2bb6ff transition-all duration-300"
                            style={{ width: `${teamsPct}%` }}
                        />
                    </div>
                </div>
            )}

            {/* TM refresh status */}
            {tmStatus && tmStatus.phase && tmStatus.phase !== "never" && (
                <div className="op-card p-4 mb-6 border-l-2 border-te8ff00" data-testid="tm-status">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="op-label text-te8ff00">
                            // AGGIORNAMENTO TM ({tmStatus.source || "transfermarkt.it"}) · {tmStatus.running ? "IN CORSO" : (tmStatus.phase === "done" ? "COMPLETATO" : tmStatus.phase)}
                        </div>
                        <div className="op-mono text-xs text-tc9ccd3">
                            {(tmStatus.players_done ?? 0).toLocaleString("it-IT")} / {(tmStatus.players_total ?? 0).toLocaleString("it-IT")} giocatori
                            {tmStatus.updated != null && <span className="text-t8f95a3"> · {tmStatus.updated} valori aggiornati</span>}
                            {tmStatus.failed ? <span className="text-tff7a1a"> · {tmStatus.failed} non letti</span> : null}
                        </div>
                    </div>
                    {tmStatus.running && tmStatus.players_total > 0 && (
                        <div className="h-2 bg-t1a1d24 border border-t22252a mt-2">
                            <div
                                className="h-full bg-te8ff00 transition-all duration-300"
                                style={{ width: `${Math.min(100, (tmStatus.players_done / tmStatus.players_total) * 100)}%` }}
                            />
                        </div>
                    )}
                    {tmStatus.error && <div className="text-tff3b30 text-xs mt-2">{tmStatus.error}</div>}
                </div>
            )}

            {/* History import status */}
            {histStatus && histStatus.phase && histStatus.phase !== "never" && (
                <div className="op-card p-4 mb-6 border-l-2 border-t2bb6ff" data-testid="history-status">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="op-label text-t2bb6ff">
                            // STORICO CARRIERA · {histStatus.running ? "IN CORSO" : (histStatus.phase === "done" ? "COMPLETATO" : histStatus.phase)}
                        </div>
                        <div className="op-mono text-xs text-tc9ccd3">
                            {(histStatus.players_done ?? 0).toLocaleString("it-IT")} / {(histStatus.players_total ?? 0).toLocaleString("it-IT")} giocatori
                            {histStatus.updated != null && <span className="text-t8f95a3"> · {histStatus.updated} con storico</span>}
                            {histStatus.failed ? <span className="text-tff7a1a"> · {histStatus.failed} falliti</span> : null}
                        </div>
                    </div>
                    {histStatus.running && histStatus.players_total > 0 && (
                        <div className="h-2 bg-t1a1d24 border border-t22252a mt-2">
                            <div
                                className="h-full bg-t2bb6ff transition-all duration-300"
                                style={{ width: `${Math.min(100, (histStatus.players_done / histStatus.players_total) * 100)}%` }}
                            />
                        </div>
                    )}
                    {histStatus.error && <div className="text-tff3b30 text-xs mt-2">{histStatus.error}</div>}
                </div>
            )}

            {/* Actions */}
            <div className="op-card p-6">
                <div className="op-label text-t2bb6ff mb-3">// AZIONI</div>

                {!showCredentials ? (
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => { setFormMode("full"); setShowCredentials(true); }}
                            disabled={status?.running || tmStatus?.running || histStatus?.running || busy}
                            className="op-btn op-btn-primary disabled:opacity-50"
                            data-testid="legacy-start-btn"
                        >
                            Avvia import completo
                        </button>
                        <button
                            onClick={startTmRefresh}
                            disabled={status?.running || tmStatus?.running || histStatus?.running || busy}
                            className="op-btn disabled:opacity-50"
                            data-testid="tm-refresh-btn"
                            title="Legge i valori di mercato in tempo reale direttamente da transfermarkt.it (nessuna credenziale)."
                        >
                            {tmStatus?.running ? "Aggiornamento TM in corso…" : "⟳ Aggiorna TM al volo"}
                        </button>
                        <button
                            onClick={() => { setFormMode("history"); setShowCredentials(true); }}
                            disabled={status?.running || tmStatus?.running || histStatus?.running || busy}
                            className="op-btn disabled:opacity-50"
                            data-testid="legacy-history-btn"
                            title="Recupera dal vecchio sito lo storico carriera (stagione · squadra · tipo · valore) di ogni giocatore e lo mostra sulla scheda."
                        >
                            {histStatus?.running ? "Import storico in corso…" : "📜 Importa storico carriera"}
                        </button>
                        {(status?.running || tmStatus?.running || histStatus?.running) && (
                            <button
                                onClick={cancel}
                                className="op-btn op-btn-ghost text-tff3b30 hover:text-tff3b30"
                                data-testid="legacy-cancel-btn"
                            >
                                {status?.running ? "Annulla import" : (histStatus?.running ? "Sblocca / Annulla storico" : "Sblocca / Annulla TM")}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3 max-w-md">
                        <div className="op-label text-te8ff00">
                            {formMode === "history" ? "// IMPORT STORICO CARRIERA" : "// IMPORT COMPLETO"}
                        </div>
                        {formMode === "history" && (
                            <p className="text-xs text-t2bb6ff">
                                Recupera SOLO lo storico carriera (stagione · squadra · tipo · valore) di ogni giocatore. Non tocca rose, budget o stipendi.
                            </p>
                        )}
                        <div>
                            <label className="op-label block mb-1">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="op-input w-full"
                                data-testid="legacy-username-input"
                                autoComplete="username"
                            />
                        </div>
                        <div>
                            <label className="op-label block mb-1">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="op-input w-full"
                                data-testid="legacy-password-input"
                                autoComplete="current-password"
                                onKeyDown={(e) => { if (e.key === "Enter") (formMode === "history" ? startHistoryImport() : startImport()); }}
                            />
                        </div>
                        <div className="flex gap-3">
                            {formMode === "history" ? (
                                <button
                                    onClick={startHistoryImport}
                                    disabled={busy}
                                    className="op-btn op-btn-primary disabled:opacity-50"
                                    data-testid="legacy-history-submit-btn"
                                >
                                    {busy ? "Avvio…" : "📜 Avvia import storico"}
                                </button>
                            ) : (
                                <button
                                    onClick={startImport}
                                    disabled={busy}
                                    className="op-btn op-btn-primary disabled:opacity-50"
                                    data-testid="legacy-submit-btn"
                                >
                                    {busy ? "Avvio…" : "Conferma e avvia"}
                                </button>
                            )}
                            <button
                                onClick={() => { setShowCredentials(false); setPassword(""); }}
                                className="op-btn op-btn-ghost"
                                data-testid="legacy-cancel-form-btn"
                            >
                                Annulla
                            </button>
                        </div>
                        <p className="text-xs text-t8f95a3 mt-2">
                            Le credenziali sono usate solo per la sessione di scraping e non vengono salvate.
                        </p>
                    </div>
                )}
            </div>

            {/* Imported teams table */}
            {legacyTeams.length > 0 && (
                <div className="op-card p-6 mt-6">
                    <div className="op-label text-t2bb6ff mb-3">// SQUADRE LEGACY IMPORTATE</div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-t22252a text-t8f95a3">
                                    <th className="text-left py-2 pr-3 op-label">#</th>
                                    <th className="text-left py-2 pr-3 op-label">Nome legacy</th>
                                    <th className="text-left py-2 pr-3 op-label">Manager</th>
                                    <th className="text-right py-2 pr-3 op-label">Budget</th>
                                    <th className="text-right py-2 pr-3 op-label">Ingaggi</th>
                                    <th className="text-center py-2 pr-3 op-label">Rosa</th>
                                    <th className="text-center py-2 pr-3 op-label">Ugly</th>
                                </tr>
                            </thead>
                            <tbody>
                                {legacyTeams.map((t) => (
                                    <tr key={t.legacy_id} className="border-b border-t1a1d24 hover:bg-t1a1d24/40">
                                        <td className="py-2 pr-3 op-mono text-t8f95a3">{t.legacy_id}</td>
                                        <td className="py-2 pr-3 text-tffffff">{t.name}</td>
                                        <td className="py-2 pr-3 text-tc9ccd3">{t.managerName || "—"}</td>
                                        <td className="py-2 pr-3 text-right op-mono text-t2bb6ff">
                                            {t.balance != null ? `${(t.balance / 1_000_000).toFixed(1)}mln` : "—"}
                                        </td>
                                        <td className="py-2 pr-3 text-right op-mono text-te8ff00">
                                            {t.totalWages != null ? `${(t.totalWages / 1_000_000).toFixed(1)}mln` : "—"}
                                        </td>
                                        <td className="py-2 pr-3 text-center op-mono">{t.numberOfPlayers ?? "—"}</td>
                                        <td className="py-2 pr-3 text-center op-mono text-tff6b00">{t.numberOfUglies ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="op-card p-6 mt-6" data-testid="tm-migration-card">
                <div className="op-label text-tff7a1a mb-3">// MIGRAZIONE VALORI TM (anteprima → produzione)</div>
                <p className="text-sm text-tc9ccd3 mb-4 leading-relaxed">
                    {"Usa questo se Transfermarkt blocca il server di produzione. Esegui \"Aggiorna TM\" dove funziona (anteprima), poi scarica i valori e importali qui. Aggiorna solo il valore live (e quindi il \"Valore TM\" mostrato e lo stipendio arancione di acquisto/rinnovo), senza toccare i contratti né gli stipendi applicati."}
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                    <button onClick={exportTmValues} disabled={tmMigBusy} className="op-btn op-btn-ghost disabled:opacity-50" data-testid="tm-export-btn">
                        {tmMigBusy ? "Attendere…" : "⬇︎ Scarica valori TM"}
                    </button>
                    <button onClick={() => importFileRef.current?.click()} disabled={tmMigBusy} className="op-btn op-btn-primary disabled:opacity-50" data-testid="tm-import-btn">
                        {tmMigBusy ? "Attendere…" : "⬆︎ Importa valori TM (da file)"}
                    </button>
                    <input ref={importFileRef} type="file" accept="application/json,.json" className="hidden"
                        onChange={(e) => importTmValues(e.target.files?.[0])} data-testid="tm-import-file" />
                </div>
                {tmMigResult && (
                    <div className="op-card mt-4 p-3 text-sm" style={{ borderLeft: "2px solid rgb(var(--tc-7ce38b))" }} data-testid="tm-migration-result">
                        {tmMigResult.kind === "export"
                            ? <span className="text-t7ce38b">Esportati {tmMigResult.count} valori TM. File scaricato.</span>
                            : <span className="text-tc9ccd3">
                                Importati: <b className="text-t7ce38b">{tmMigResult.applied_links}</b> giocatori aggiornati
                                {" "}({tmMigResult.rows_modified} righe modificate).
                                {tmMigResult.unknown_links > 0 && <span className="text-tff7a1a"> {tmMigResult.unknown_links} link non trovati nel DB locale.</span>}
                              </span>}
                    </div>
                )}
            </div>

            <div className="op-card p-6 mt-6">
                <div className="op-label text-t8f95a3 mb-3">// COME FUNZIONA</div>
                <ul className="text-tc9ccd3 text-sm space-y-2 leading-relaxed list-disc list-inside">
                    <li>{"L'import gira in background sul server: puoi chiudere la pagina, continua comunque."}</li>
                    <li>Recupera dal vecchio sito: rose complete (25 giocatori + Ugly), anni di contratto, ingaggi, budget e valore di mercato.</li>
                    <li>Ogni giocatore viene linkato al PES DB tramite il suo PES ID, garantendo un match 1:1.</li>
                    <li>{"L'import è idempotente: puoi rilanciarlo, aggiornerà i dati senza duplicare nulla."}</li>
                    <li>Le squadre vengono matchate per nome (case-insensitive): se un nome non combacia, il club mostra giocatori in <code>legacy_players</code> ma niente <code>roster_entries</code>.</li>
                </ul>
            </div>
        </div>
    );
}

function Stat({ label, value, accent = "rgb(var(--tc-ffffff))" }) {
    return (
        <div className="p-5">
            <div className="op-label">{label}</div>
            <div className="mt-2 op-mono font-bold text-2xl" style={{ color: accent }}>
                {value}
            </div>
        </div>
    );
}
